import { CopyTypes } from '../types/journey-context'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { BlueConicRecommendedNotificationParams } from '@pushly/models/lib/structs/journeys'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import { DelayType, StepType } from '@pushly/models/lib/enums/journeys'
import { CatalogItemPickStrategy, ItemRankingStrategy } from '@pushly/models/lib/enums/catalogs'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import {
    isActionStep,
    isDelayStep,
    LinearAddStepType,
} from '../types/journey-nodes'
import type { IActionStep, IDelayStep } from '@pushly/models/lib/structs/journeys'

export function getBaseConfigurationForStepType(
    nodeType: LinearAddStepType,
    stepId: string,
    outStepId: number | string,
    addtOptions?: {
        overrideData?: IActionStep | IDelayStep
        overrideType?: CopyTypes
        meta?: Record<string, any>
        channels?: DeliveryChannel[]
    },
): IDelayStep | IActionStep {
    const actionIdx = addtOptions?.meta?.actionIndex

    switch (nodeType) {
        case StepType.DELAY: {
            const override = addtOptions?.overrideData && isDelayStep(addtOptions.overrideData)
                ? addtOptions.overrideData
                : undefined
            if (override) {
                return {
                    ...override,
                    id: stepId,
                    configuration: {
                        ...override.configuration,
                        out_step_id: outStepId,
                    },
                }
            }
            return {
                id: stepId,
                type: StepType.DELAY,
                configuration: {
                    type: DelayType.RELATIVE,
                    params: {
                        delay_seconds: 60 * 60 * 24 * 2, // 2 days
                        qualifier: RelativeDateDisplayMetric.DAYS,
                    },
                    out_step_id: outStepId,
                },
                // propagate meta supplied by caller (e.g., actionIndex)
                meta: { ...(addtOptions?.meta ?? {}) },
            }
        }

        case JourneyActionType.SEND_NOTIFICATION: {
            const override = addtOptions?.overrideData && isActionStep(addtOptions.overrideData)
                ? addtOptions.overrideData
                : undefined
            let overrideParams = override?.configuration?.params
            if (override && addtOptions?.overrideType === CopyTypes.COPY) {
                const { notification_id, ...notification } = override.configuration.params ?? {}
                overrideParams = notification
            }

            if (override) {
                return {
                    ...override,
                    id: stepId,
                    configuration: {
                        type: nodeType,
                        params: overrideParams ?? {},
                        out_step_id: outStepId,
                    },
                    meta: { ...(override.meta ?? {}), ...(addtOptions?.meta ?? {}) },
                }
            }

            return {
                id: stepId,
                type: StepType.ACTION,
                configuration: {
                    type: nodeType,
                    params: {},
                    out_step_id: outStepId,
                },
                meta: { ...(addtOptions?.meta ?? {}) },
            }
        }

        case JourneyActionType.SEND_CART_NOTIFICATION:
        case JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION:
        case JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION:
        case JourneyActionType.SEND_RANKED_ITEM_NOTIFICATION:
        case JourneyActionType.SEND_COMPLETED_ITEM_NOTIFICATION: {
            const override = addtOptions?.overrideData && isActionStep(addtOptions.overrideData)
                ? addtOptions.overrideData
                : undefined
            let overrideParams = override?.configuration?.params
            if (override && addtOptions?.overrideType === CopyTypes.COPY) {
                const { notification_id, ...rest } = override.configuration.params ?? {}
                overrideParams = rest
            }

            if (override) {
                return {
                    ...override,
                    id: stepId,
                    configuration: {
                        type: nodeType,
                        params: { ...overrideParams },
                        out_step_id: outStepId,
                    },
                    meta: { ...(override.meta ?? {}), ...(addtOptions?.meta ?? {}) },
                }
            }

            // Default strategy differs by action type
            const defaultStrategy =
                nodeType === JourneyActionType.SEND_RANKED_ITEM_NOTIFICATION
                    ? ItemRankingStrategy.VIEWED_RANK_7D
                    : ((actionIdx ?? 0) > 0
                        ? CatalogItemPickStrategy.PREVIOUSLY_SELECTED
                        : CatalogItemPickStrategy.MOST_RECENT)

            return {
                id: stepId,
                type: StepType.ACTION,
                configuration: {
                    type: nodeType,
                    params: { item_strategy: defaultStrategy },
                    out_step_id: outStepId,
                },
                meta: { ...(addtOptions?.meta ?? {}) },
            }
        }

        case JourneyActionType.SEND_RECOMMENDED_NOTIFICATION: {
            const override = addtOptions?.overrideData && isActionStep(addtOptions.overrideData)
                ? addtOptions.overrideData
                : undefined

            if (override) {
                return {
                    ...override,
                    id: stepId,
                    configuration: {
                        ...override.configuration,
                        out_step_id: outStepId,
                    },
                    meta: { ...(override.meta ?? {}), ...(addtOptions?.meta ?? {}) },
                }
            }

            return {
                id: stepId,
                type: StepType.ACTION,
                configuration: {
                    type: nodeType,
                    // BlueConicRecommendedNotificationParams is a class (AbstractStruct) but params
                    // expects a plain object. Structural mismatch requires `as any` until js-models
                    // exports a plain interface for this type. Tracked in consolidation spec.
                    params: new BlueConicRecommendedNotificationParams() as any,
                    out_step_id: outStepId,
                },
                meta: { ...(addtOptions?.meta ?? {}) },
            }
        }

        default: {
            const _exhaustive: never = nodeType
            throw new Error(`Unhandled step type: ${_exhaustive}`)
        }
    }
}
